/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { PDFCheckBox, PDFDocument, PDFDropdown, PDFRadioGroup, PDFTextField, StandardFonts } from "pdf-lib";
import { Document, Page } from "react-pdf";
import FormField from "pocs/pdf-lib/fill-form/components/FormField";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import LoadingPage from "components/common_components/LoadingPage";
import { useMediaQuery, useTheme } from "@material-ui/core";
import { useDispatch } from "react-redux";
import clsx from "clsx";
import { FileText } from "lucide-react";
import { notifyError } from "redux/actions/Notifications";
import { useForceMenuCollapsed } from "components/Menu/MenuContext";
import {
    splitTextForAddendum,
    parseDefaultAppearance,
    cleanFieldText,
    getFieldTextBounds,
    getFieldMaxLines,
    getWidgetBorderWidth,
    fitSingleLineText,
} from "../utils/pdfTextFitting";
import { createRuledLayoutResolver, getRuledTextBox, ruledTextAppearanceProvider } from "../utils/pdfRuledFields";
import { getFieldPageAndRect, setFieldFontSize } from "../utils/pdfFieldGeometry";
import {
    buildAddendumLabel,
    buildManualAddendumLabel,
    buildReferenceSuggestions,
    collectFormSections,
    findReferenceSuggestion,
} from "../utils/pdfAddendumLabels";
import { renderAddendum } from "../utils/pdfAddendum";
import {
    OverflowPolicy,
    buildOverflowPolicies,
    getOverflowPolicy,
    removeMaxLength,
    withinMaxLength,
} from "../utils/overflowPolicy";
import AddendumPanel, { getAddendumRank, sortAddendums } from "./AddendumPanel";
import PdfPagination from "./PdfPagination";

// Colors of the highlighted fields, by highlight kind (status success/warning/error colors)
const HIGHLIGHT_COLORS = {
    copied: { background: "#eaf7eb", border: "#155724" },
    warning: { background: "#fff3cd", border: "#856404" },
    notCopied: { background: "#f8d7da", border: "#721c24" },
};

/** Scrolls the PDF to a field and flashes it. Returns false when the field is not drawn. */
export const locateField = (fieldName) => {
    const fieldElement = fieldName && document.querySelector(`[data-field-name="${CSS.escape(fieldName)}"]`);
    if (!fieldElement) return false;
    fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
    fieldElement.classList.add("located-from-addendum");
    setTimeout(() => fieldElement.classList.remove("located-from-addendum"), 3000);
    return true;
};

// Room kept beside the page inside the viewer: its vertical scrollbar and some breathing space
const PAGE_GUTTER = 32;
// Below this the form's fields become too small to fill in; the viewer scrolls sideways instead
const MIN_PAGE_WIDTH = 560;

/**
 * Width of an element, kept up to date. The first value is read before paint; later changes are
 * debounced, because every new width rebuilds the PDF's field overlay.
 */
const useElementWidth = (ref, delay = 150) => {
    const [width, setWidth] = useState(null);

    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) return undefined;
        setWidth(element.clientWidth);

        let timer;
        const observer = new ResizeObserver(() => {
            clearTimeout(timer);
            timer = setTimeout(() => setWidth(element.clientWidth), delay);
        });
        observer.observe(element);
        return () => {
            clearTimeout(timer);
            observer.disconnect();
        };
    }, []);

    return width;
};

const PdfForm = ({
    pdfUrl,
    clientValues,
    formValues,
    onSubmit = () => {},
    mode,
    downloadFileNameGetter = () => "form.pdf",
    fieldTriggers = {},
    addendumInfo = null,
    addendums = [],
    onAddendumsChange = () => {},
    // { [pdfFieldName]: "copied" | "warning" | "notCopied" }
    fieldHighlights = {},
    // Mapeos de la versión del formulario: de ahí sale la política de cada campo para
    // los valores que exceden su límite de caracteres (ver overflowPolicy)
    fieldMappings = [],
    // Extra tab next to the addendums: { label, count, icon, content }
    sidePanel = null,
}) => {
    const [sideTab, setSideTab] = useState("extra");
    const [fields, setFields] = useState({});
    // Sections of the form, offered as references while writing a manual addendum
    const [sections, setSections] = useState([]);
    const referenceSuggestions = useMemo(() => buildReferenceSuggestions(sections), [sections]);
    const [isEditingAddendum, setIsEditingAddendum] = useState(false);
    const [numPages, setNumPages] = useState(null);
    // The element of react-pdf's <Document> that scrolls through the pages
    const [documentElement, setDocumentElement] = useState(null);
    const [values, setValues] = useState({});
    const [printLoading, setPrintLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
    // The page is drawn at its preferred size when it fits, and shrinks to the viewer's width when it
    // does not (the addendum panel takes part of the screen). The field overlay is laid out at the
    // same width, so both always match.
    const viewerRef = useRef(null);
    const viewerWidth = useElementWidth(viewerRef);
    const preferredPageWidth = isMediumScreen ? 800 : 1152;
    const pageWidth = viewerWidth
        ? Math.floor(Math.max(MIN_PAGE_WIDTH, Math.min(preferredPageWidth, viewerWidth - PAGE_GUTTER)))
        : null;
    const readOnly = mode === "view";
    const overflowPolicies = useMemo(() => buildOverflowPolicies(fieldMappings), [fieldMappings]);

    // Fields are drawn asynchronously, page by page: a stylesheet reaches them whenever they appear
    const highlightStyles = useMemo(
        () =>
            Object.entries(fieldHighlights)
                .filter(([, kind]) => HIGHLIGHT_COLORS[kind])
                .map(([fieldName, kind]) => {
                    const { background, border } = HIGHLIGHT_COLORS[kind];
                    // Outline, not border: native checkboxes and radios do not draw a border or a background
                    return `.pdf-form-container [data-field-name="${CSS.escape(fieldName)}"] { background-color: ${background} !important; outline: 2px solid ${border} !important; outline-offset: 1px; }`;
                })
                .join("\n"),
        [fieldHighlights]
    );

    // The addendum panel is always docked next to the PDF: give the viewer the menu's width
    useForceMenuCollapsed(true);

    const getFieldValue = (fieldName) => {
        return values[fieldName] || "";
    };

    const setFieldValue = (fieldName, value) => {
        setValues((prev) => ({ ...prev, [fieldName]: value }));
        // Trigger dependent fields if any
        if (fieldTriggers[fieldName]) {
            const dependentField = fieldTriggers[fieldName];
            setValues((prev) => ({ ...prev, [dependentField]: value }));
        }
    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    useEffect(() => {
        // El estado del PDF anterior se descarta ANTES de que react-pdf monte el
        // nuevo documento. `pdfUrl` vive en el contexto de Forms y sobrevive a la
        // navegacion, asi que al entrar aqui con otro formulario se conservaba el
        // numPages del anterior: se seguian renderizando sus <Page>, y su getPage()
        // caia sobre un worker ya destruido ("Cannot read properties of null
        // (reading 'sendWithPromise')").
        setNumPages(null);
        setFields({});
        setSections([]);
    }, [pdfUrl]);

    useEffect(() => {
        if (!pdfUrl || !pageWidth) return undefined;

        // prepareFields es asincrono: si pdfUrl o el ancho cambian mientras esta en
        // vuelo, la respuesta lenta no debe sobrescribir los campos vigentes.
        let cancelled = false;
        prepareFields(() => cancelled);
        return () => {
            cancelled = true;
        };
    }, [pdfUrl, pageWidth]);

    useEffect(() => {
        if (clientValues) {
            setValues((prev) => ({ ...prev, ...clientValues }));
            Object.keys(clientValues).forEach((key) => {
                document.querySelectorAll(`*[data-field-name="${key}"]`).forEach((el) => {
                    el.style.backgroundColor = "#eaf7eb";
                    el.style.border = "1px solid #155724";
                });
            });
        }
    }, [clientValues]);

    useEffect(() => {
        if (formValues) {
            setValues(formValues);
        }
    }, [formValues]);

    async function prepareFields(isCancelled) {
        try {
            await buildFields(isCancelled);
        } catch (error) {
            if (isCancelled()) return;
            console.error("Error al preparar los campos del PDF:", error);
            dispatch(notifyError(error.message || "Unexpected error loading the form fields."));
        }
    }

    async function buildFields(isCancelled) {
        const existingPdfBytes = await fetch(pdfUrl).then((res) => res.arrayBuffer());
        const pdfDoc = await PDFDocument.load(existingPdfBytes, { ignoreEncryption: true });
        // Antes de getForm(), que borra el XFA (ver generatePDF).
        const getRuledLayout = createRuledLayoutResolver(pdfDoc);
        const form = pdfDoc.getForm();

        const pages = pdfDoc.getPages();
        const fieldData = pages.reduce((map, _, idx) => {
            map[idx] = [];
            return map;
        }, {});

        const fields = form.getFields();

        if (isCancelled()) return;

        const isBarcodeField = (field) => {
            return field instanceof PDFTextField && field.acroField.getFullyQualifiedName().includes('PDF417BarCode');
        };

        fields
            .filter((f) => (!f.isReadOnly()) || (f.isReadOnly() && !isBarcodeField(f)))
            .forEach((field) => {
                FormField({
                    pages,
                    field,
                    pdfScale: 1,
                    pdfWidth: pageWidth,
                    ruledLayout: getRuledLayout(field),
                }).forEach((f) => {
                    const { pageIndex, component } = f;
                    // pageIndex es -1 cuando el widget no pudo ubicarse en una pagina.
                    if (!fieldData[pageIndex]) return;
                    fieldData[pageIndex].push(component);
                });
            });

        setFields(fieldData);
        setSections(collectFormSections(fields, pages));
    }

    const onSubmitHandler = (event) => {
        event.preventDefault();
        // An open addendum editor is not part of the form yet: saving now would silently drop it
        if (isEditingAddendum) {
            dispatch(notifyError("Finish or cancel the addendum you are editing before saving the form."));
            return;
        }
        onSubmit(event, values);
    };

    const locateAddendum = (addendum) => {
        const fieldName = findReferenceSuggestion(addendum.reference, referenceSuggestions)?.fieldName;
        if (locateField(fieldName)) return;
        document
            .querySelector(`.pdf-form-container .react-pdf__Page[data-page-number="${addendum.pageNumber}"]`)
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const generatePDF = async () => {
        try {
            const existingPdfBytes = await fetch(pdfUrl).then((res) => res.arrayBuffer());
            const pdfDoc = await PDFDocument.load(existingPdfBytes, {
                ignoreEncryption: true,
                throwOnInvalidObject: false,
                updateMetadata: false,
            });

            // Antes de getForm(), que borra el XFA: de ahí sale el interlineado de los
            // campos con renglones impresos (o, sin XFA, de los renglones de la página).
            const getRuledLayout = createRuledLayoutResolver(pdfDoc);
            const form = pdfDoc.getForm();
            const pdfFields = form.getFields();
            const pages = pdfDoc.getPages();
            // Helvetica es la fuente con la que pdf-lib dibuja TODOS los campos al
            // hacer form.flatten() (usa la fuente por defecto del formulario, ignorando
            // la declarada en el /DA), así que es la fuente correcta para medir.
            // Times se usa solo para el encabezado y los títulos del addendum.
            const [helveticaFont, timesFont, timesBoldFont] = await Promise.all([
                pdfDoc.embedFont(StandardFonts.Helvetica),
                pdfDoc.embedFont(StandardFonts.TimesRoman),
                pdfDoc.embedFont(StandardFonts.TimesRomanBold),
            ]);

            // Deshabilitar el formato enriquecido en todos los campos de texto
            pdfFields.forEach((field) => {
                try {
                    if (field instanceof PDFTextField) {
                        field.disableRichFormatting();
                    }
                } catch (error) {
                    console.warn(`Error procesando campo ${field?.getName?.()}:`, error.message);
                }
            });

            // PRIMERA PASADA: Establecer valores y detectar overflows en campos multiline.
            // El campo conserva todo el texto que quepa; el sobrante se acumula para el
            // addendum que se dibuja al final del documento.
            const addendumEntries = []; // [{ pageIndex, rect, label, text }]
            const pushAddendumEntry = (field, { pageIndex, rect }, text) =>
                addendumEntries.push({
                    pageIndex,
                    rect,
                    text,
                    label: buildAddendumLabel({ formCode: addendumInfo?.formCode, pageNumber: pageIndex + 1, field }),
                });

            Object.keys(values).forEach((key) => {
                try {
                    const field = form.getField(key);
                    if (!field) {
                        console.warn(`Campo no encontrado: ${key}`);
                        return;
                    }

                    if (field instanceof PDFTextField) {
                        field.disableRichFormatting();
                        // Se normaliza igual que pdf-lib antes de maquetar, para que los
                        // offsets con los que se corta el texto coincidan con los suyos.
                        const text = cleanFieldText(values[key]);

                        // /MaxLen es un límite duro del PDF (setText lanza si se excede). La
                        // política del mapeo decide qué pasa con lo que no cabe; con
                        // "ignoreMaxLength" el límite se quita antes de escribir.
                        const policy = getOverflowPolicy(overflowPolicies[key]);
                        if (policy === OverflowPolicy.IGNORE_MAX_LENGTH) removeMaxLength(field);
                        const maxLength = field.getMaxLength();
                        const isOverLimit = maxLength > 0 && text.length > maxLength;
                        const limitedText = isOverLimit ? text.slice(0, maxLength) : text;
                        // Lo cortado por el límite solo pasa al addendum con la política "addendum"
                        const addsOverLimitToAddendum = isOverLimit && policy === OverflowPolicy.ADDENDUM;

                        if (field.isMultiline()) {
                            const widgetInfo = getFieldPageAndRect(field, pages);
                            // pageIndex < 0 cuando el widget no tiene un /P resoluble.
                            if (!widgetInfo || widgetInfo.pageIndex < 0) {
                                field.setText(limitedText);
                                return;
                            }

                            const { rect, widget } = widgetInfo;
                            const { fontSize, isAutoSize } = parseDefaultAppearance(field);
                            const ruledLayout = getRuledLayout(field);
                            let fitted, remainder, didOverflow;

                            if (ruledLayout) {
                                // Campo con renglones: se mide y se dibuja con su interlineado,
                                // porque el de pdf-lib (1.2 x la altura de la fuente) separa el
                                // texto de los renglones un poco más en cada línea.
                                const box = getRuledTextBox(ruledLayout, rect, getWidgetBorderWidth(widget));
                                ({ fitted, remainder, didOverflow } = splitTextForAddendum(
                                    limitedText, helveticaFont, fontSize, box.width, box.maxLines
                                ));
                                field.setText(withinMaxLength(fitted, maxLength));
                                // Deja el campo como actualizado, así flatten() no regenera
                                // su apariencia con el proveedor por defecto.
                                field.updateAppearances(helveticaFont, ruledTextAppearanceProvider(box, fontSize));
                            } else {
                                const bounds = getFieldTextBounds(widget, rect);
                                const maxLines = getFieldMaxLines(helveticaFont, fontSize, bounds.height);

                                // splitTextForAddendum reserva un margen derecho y devuelve el
                                // texto visible con los saltos de línea ya aplicados, para que
                                // pdf-lib no lo reajuste contra el borde del recuadro.
                                ({ fitted, remainder, didOverflow } = splitTextForAddendum(
                                    limitedText, helveticaFont, fontSize, bounds.width, maxLines, { isAutoSize }
                                ));

                                field.setText(withinMaxLength(fitted, maxLength));

                                // Con "0 Tf" pdf-lib encogería el texto hasta que quepa en vez
                                // de recortarlo; al truncar hay que fijar el tamaño medido para
                                // que lo dibujado coincida con lo calculado.
                                if (didOverflow && isAutoSize) setFieldFontSize(field, fontSize);
                            }

                            // Si el límite cortó el texto, el addendum lleva el valor completo
                            // y no solo lo que no cupo en el recuadro.
                            if (addsOverLimitToAddendum) pushAddendumEntry(field, widgetInfo, text);
                            else if (didOverflow) pushAddendumEntry(field, widgetInfo, remainder);
                        } else {
                            const widgetInfo = getFieldPageAndRect(field, pages);
                            const { fontSize, isAutoSize } = parseDefaultAppearance(field);

                            // Con "0 Tf" pdf-lib encoge el texto por su cuenta y nunca lo
                            // recorta por ancho; solo /MaxLen lo corta. Sin un widget ubicable
                            // no hay a qué referenciar desde el addendum.
                            if (!widgetInfo || widgetInfo.pageIndex < 0 || isAutoSize) {
                                field.setText(limitedText);
                                if (addsOverLimitToAddendum && widgetInfo?.pageIndex >= 0) {
                                    pushAddendumEntry(field, widgetInfo, text);
                                }
                                return;
                            }

                            const { rect, widget } = widgetInfo;
                            const bounds = getFieldTextBounds(widget, rect);

                            // pdf-lib no envuelve ni encoge un campo de una línea con tamaño
                            // fijo: dibuja todo y el clip se come el sobrante, perdiendo
                            // caracteres. Se encoge hasta el piso legible y, si aún no cabe,
                            // la casilla queda con un fragmento marcado y el valor íntegro
                            // pasa al addendum.
                            // Se mide lo que de verdad se escribe: medir el valor completo y luego
                            // cortarlo a /MaxLen encogería la letra sin necesidad.
                            const fit = fitSingleLineText(limitedText, helveticaFont, fontSize, bounds.width);
                            field.setText(withinMaxLength(fit.text, maxLength));
                            setFieldFontSize(field, fit.fontSize);

                            // La casilla solo muestra un fragmento, así que el addendum lleva el
                            // valor completo y no un remanente. Lo que no cabe a lo ancho va
                            // siempre; lo cortado por /MaxLen, según la política.
                            if (fit.didOverflow || addsOverLimitToAddendum) {
                                pushAddendumEntry(field, widgetInfo, text);
                            }
                        }
                    } else if (field instanceof PDFDropdown) {
                        field.select(values[key]);
                    } else if (field instanceof PDFRadioGroup) {
                        // select() lanza si la opcion no pertenece al grupo (p.ej. un valor
                        // guardado con un PDF anterior), asi que se valida antes.
                        const option = values[key];
                        if (option && field.getOptions().includes(option)) {
                            field.select(option);
                        }
                    } else if (field instanceof PDFCheckBox) {
                        // El valor puede llegar como booleano o como el string "true"/"false".
                        // check() de pdf-lib no acepta argumentos (siempre marca), por lo que
                        // hay que decidir explícitamente entre check() y uncheck().
                        const isChecked = values[key] === true || String(values[key]).toLowerCase() === "true";
                        if (isChecked) {
                            field.check();
                        } else {
                            field.uncheck();
                        }
                    }
                } catch (error) {
                    console.error(`Error al establecer valor para ${key}:`, error);
                }
            });

            // Aplanar el formulario: convierte todos los campos (text, checkbox, dropdown)
            // en contenido estático de la página, antes de añadir las páginas del addendum
            // (que así no arrastran widgets del AcroForm).
            form.flatten();

            // Los addendums escritos a mano se intercalan por página, después de los
            // automáticos de esa página; los de una referencia que el PDF no reconoce (sin
            // página) van detrás de todas las páginas, y los generales (sin referencia) al final.
            sortAddendums(addendums).forEach((addendum, order) => {
                addendumEntries.push({
                    manual: true,
                    order,
                    pageIndex: getAddendumRank(addendum) - 1,
                    text: addendum.text,
                    label: buildManualAddendumLabel({
                        formCode: addendumInfo?.formCode,
                        pageNumber: addendum.pageNumber,
                        reference: addendum.reference,
                    }),
                });
            });

            // SEGUNDA PASADA: Volcar el texto sobrante en el addendum, al final del
            // documento, en orden de lectura: página, luego arriba->abajo, izq->der.
            if (addendumEntries.length) {
                addendumEntries.sort((a, b) => {
                    if (a.pageIndex !== b.pageIndex) return a.pageIndex - b.pageIndex;
                    if (a.manual || b.manual) return a.manual === b.manual ? a.order - b.order : a.manual ? 1 : -1;
                    return b.rect.y - a.rect.y || a.rect.x - b.rect.x;
                });

                renderAddendum(pdfDoc, addendumEntries, addendumInfo || {}, {
                    body: helveticaFont,
                    title: timesFont,
                    headerBold: timesBoldFont,
                });
            }

            const pdfBytes = await pdfDoc.save({ useObjectStreams: false });
            const blob = new Blob([pdfBytes], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            return url;
        } catch (error) {
            console.error("Error general al generar el PDF:", error);
            throw error;
        }
    };

    const onPrint = async () => {
        setPrintLoading(true);
        try {
            // La ventana abierta sigue usando el object URL, así que no se revoca aquí.
            const url = await generatePDF();
            window.open(url);
        } catch (error) {
            dispatch(notifyError(error.message || "Unexpected error generating the PDF."));
        } finally {
            setPrintLoading(false);
        }
    };

    const onDownload = async () => {
        setDownloadLoading(true);
        try {
            const url = await generatePDF();
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", downloadFileNameGetter());
            document.body.appendChild(link);
            link.click();
            link.remove();
            // Se revoca en diferido: revocarlo en el mismo tick aborta la descarga
            // en algunos navegadores.
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (error) {
            dispatch(notifyError(error.message || "Unexpected error generating the PDF."));
        } finally {
            setDownloadLoading(false);
        }
    };

    const addendumPanel = (
        <AddendumPanel
            addendums={addendums}
            suggestions={referenceSuggestions}
            formCode={addendumInfo?.formCode}
            readOnly={readOnly}
            onChange={onAddendumsChange}
            onLocate={locateAddendum}
            onEditingChange={setIsEditingAddendum}
        />
    );

    return (
        <div className="pdf-form-layout">
            {highlightStyles && <style>{highlightStyles}</style>}
            <div className="pdf-form-container">
                <div className="pdf-form-container__actions">
                    {readOnly && (
                        <Button secondary small onClick={onPrint} title="Print PDF" loading={printLoading}>
                            <Icon name="printer" size="md" color="white" />
                        </Button>
                    )}
                    {readOnly && (
                        <Button secondary small onClick={onDownload} title="Download PDF" loading={downloadLoading}>
                            <Icon name="download" size="md" color="white" />
                        </Button>
                    )}
                </div>
                <form ref={viewerRef} action="#" id="pdf-overlay-form" onSubmit={onSubmitHandler} autoComplete="off">
                    {!pdfUrl && <LoadingPage />}
                    {pdfUrl && (
                        <Document
                            // Remonta todo el arbol al cambiar de PDF, en vez de dejar que los
                            // <Page> del documento anterior convivan con el nuevo worker.
                            key={pdfUrl}
                            file={pdfUrl}
                            inputRef={setDocumentElement}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={<LoadingPage />}
                        >
                            {Array.from(new Array(numPages), (el, pageIndex) => (
                                <Page
                                    key={`page_${pageIndex + 1}`}
                                    pageNumber={pageIndex + 1}
                                    width={pageWidth}
                                >
                                    {fields[String(pageIndex)]?.map((component, fieldIndex) => {
                                        const Component = component;
                                        return (
                                            <Component
                                                key={`page_${pageIndex}_field_${fieldIndex}`}
                                                valueGetter={(fieldName) => getFieldValue(fieldName)}
                                                onChange={setFieldValue}
                                                readOnly={readOnly}
                                                overflowPolicyGetter={(fieldName) => overflowPolicies[fieldName]}
                                            />
                                        );
                                    })}
                                </Page>
                            ))}
                        </Document>
                    )}
                    <PdfPagination scrollElement={documentElement} numPages={numPages} layoutKey={pageWidth} />
                </form>
            </div>
            {sidePanel ? (
                <div className="pdf-side-tabs">
                    <div className="pdf-side-tabs__bar" role="tablist">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={sideTab === "extra"}
                            className={clsx("pdf-side-tabs__tab", { active: sideTab === "extra" })}
                            onClick={() => setSideTab("extra")}
                        >
                            {sidePanel.icon}
                            <span>{sidePanel.label}</span>
                            <span className="pdf-side-tabs__count">{sidePanel.count}</span>
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={sideTab === "addendums"}
                            className={clsx("pdf-side-tabs__tab", { active: sideTab === "addendums" })}
                            onClick={() => setSideTab("addendums")}
                        >
                            <FileText size={16} />
                            <span>Addendums</span>
                            <span className="pdf-side-tabs__count">{addendums.length}</span>
                        </button>
                    </div>
                    {/* Both stay mounted so an addendum being written is not lost when switching tabs */}
                    <div className="pdf-side-tabs__panel" hidden={sideTab !== "extra"}>
                        {sidePanel.content}
                    </div>
                    <div className="pdf-side-tabs__panel" hidden={sideTab !== "addendums"}>
                        {addendumPanel}
                    </div>
                </div>
            ) : (
                addendumPanel
            )}
        </div>
    );
};

export default PdfForm;
